import { FIRST_AD_REQUEST_MARK } from './firstAdRequest'

/**
 * Sends the `consent-bootstrap:*` timings that `consentTiming.ts` records to
 * Datadog RUM, next to `first_ad_visible`, so the steps in front of the first
 * ad can be read per page load instead of only in the browser.
 *
 * Every measure goes out as a duration vital under its own name, tagged with:
 *
 * - `consent_decision`: the consent state in effect when the page load's first
 *   ad was requested, by any provider that counts toward `first_ad_visible`
 *   (display, Taboola, Outbrain, Primis). It is captured live at the request
 *   (see `markFirstAdRequest`). `no_ad_request` when the page was hidden, the
 *   visitor navigated away or the cap passed before any ad was requested.
 * - `first_ad_request`: the provider that made that request, or `none`.
 * - `initial_consent_decision`: the first decision the page reached, or `none`.
 *
 * The timings are held until the first ad request settles the tags, then sent
 * with them on each vital, so a timing recorded before the decision is tagged
 * the same as the rest of its page load. The landing view gets the same values.
 */

const PREFIX = 'consent-bootstrap:'
const DECISION_MARK = `${PREFIX}decision-`
const REQUEST_MARK = `${PREFIX}${FIRST_AD_REQUEST_MARK}:`

export const CONSENT_DECISION_CONTEXT_KEY = 'consent_decision'
export const INITIAL_CONSENT_DECISION_CONTEXT_KEY = 'initial_consent_decision'
export const FIRST_AD_REQUEST_CONTEXT_KEY = 'first_ad_request'
export const NO_AD_REQUEST = 'no_ad_request'
const NONE = 'none'

/** Past this, waiting for the first ad request says nothing about how the page loaded. */
export const CONSENT_TIMING_SETTLE_MS = 60_000

/** The two RUM calls this needs, so tests can stand in for the SDK. */
export interface ConsentTimingSink {
  addDurationVital: (
    name: string,
    options: {
      startTime: number
      duration: number
      context: Record<string, string>
    }
  ) => void
  setViewContextProperty: (key: string, value: string) => void
}

export interface ConsentTimingVitals {
  /** The visitor left the landing page: settle without its ad request, leaving the new view alone. */
  endLandingPage: () => void
  stop: () => void
}

/** Watches the consent timeline for this page load and sends each measure once, tagged as described above. */
export function startConsentTimingVitals(
  sink: ConsentTimingSink,
  timeOrigin: number = performance.timeOrigin
): ConsentTimingVitals {
  const sent = new Set<string>()
  const held: PerformanceEntry[] = []
  let initialDecision: PerformanceEntry | null = null
  let tags: Record<string, string> | null = null

  const sendMeasure = (
    entry: PerformanceEntry,
    context: Record<string, string>
  ) => {
    if (sent.has(entry.name)) return
    sent.add(entry.name)
    sink.addDurationVital(entry.name, {
      startTime: Math.round(timeOrigin + entry.startTime),
      duration: entry.duration,
      context,
    })
  }

  const settle = (
    decision: string,
    provider: string,
    { tagView }: { tagView: boolean }
  ) => {
    if (tags) return
    const settled = {
      [CONSENT_DECISION_CONTEXT_KEY]: decision,
      [FIRST_AD_REQUEST_CONTEXT_KEY]: provider,
      [INITIAL_CONSENT_DECISION_CONTEXT_KEY]:
        initialDecision?.name.slice(DECISION_MARK.length) ?? NONE,
    }
    tags = settled
    if (tagView) {
      Object.entries(settled).forEach(([key, value]) =>
        sink.setViewContextProperty(key, value)
      )
    }
    held.splice(0).forEach((entry) => sendMeasure(entry, settled))
  }

  const take = (entry: PerformanceEntry, { tagView } = { tagView: true }) => {
    if (!entry.name.startsWith(PREFIX)) return
    if (entry.entryType === 'measure') {
      if (tags) sendMeasure(entry, tags)
      else held.push(entry)
    } else if (entry.name.startsWith(DECISION_MARK)) {
      if (!initialDecision || entry.startTime < initialDecision.startTime) {
        initialDecision = entry
      }
    } else if (entry.name.startsWith(REQUEST_MARK)) {
      const [provider = NONE, state = NONE] = entry.name
        .slice(REQUEST_MARK.length)
        .split(':')
      settle(state, provider, { tagView })
    }
  }

  // Decisions first, so a request already recorded sees the initial decision.
  const recorded = [
    ...performance.getEntriesByType('mark'),
    ...performance.getEntriesByType('measure'),
  ].sort(
    (a, b) =>
      Number(b.name.startsWith(DECISION_MARK)) -
      Number(a.name.startsWith(DECISION_MARK))
  )
  recorded.forEach((entry) => take(entry))

  // No ad request before the page was hidden or the cap passed: send what was held.
  const settleWithoutRequest = () =>
    settle(NO_AD_REQUEST, NONE, { tagView: true })
  const onHidden = () => {
    if (document.visibilityState === 'hidden') settleWithoutRequest()
  }
  document.addEventListener('visibilitychange', onHidden)
  const timer = setTimeout(settleWithoutRequest, CONSENT_TIMING_SETTLE_MS)

  const observer =
    typeof PerformanceObserver === 'undefined'
      ? undefined
      : new PerformanceObserver((list) =>
          list.getEntries().forEach((entry) => take(entry))
        )
  observer?.observe({ entryTypes: ['measure', 'mark'] })

  const stop = () => {
    observer?.disconnect()
    clearTimeout(timer)
    document.removeEventListener('visibilitychange', onHidden)
  }

  return {
    // Takes what the landing page recorded but the observer has not delivered
    // yet, then stops, so a timing from the next page is never sent with the
    // landing page's tags. The RUM view has already moved on, so the tags go
    // on each vital only, even when a pending ad request settles them.
    endLandingPage: () => {
      observer
        ?.takeRecords()
        .forEach((entry) => take(entry, { tagView: false }))
      settle(NO_AD_REQUEST, NONE, { tagView: false })
      stop()
    },
    stop,
  }
}
